import { createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import { fontWeights, variants } from 'theme/typography';
import {
	type AtlasColors,
	atlasColorTokens,
	type AtlasThemeMode,
} from './tokens';

const buildAtlasVars = (colors: AtlasColors) => ({
	'--atlas-accent-primary': colors.accent.primary,
	'--atlas-accent-primary-hover': colors.accent.primaryHover,
	'--atlas-accent-primary-soft': colors.accent.primarySoft,
	'--atlas-accent-primary-alt': colors.accent.primaryAlt,
	'--atlas-accent-primary-glow': colors.accent.primaryGlow,
	'--atlas-accent-primary-glow-alpha-10': colors.accent.primaryGlowAlpha10,
	'--atlas-accent-primary-glow-alpha-20': colors.accent.primaryGlowAlpha20,
	'--atlas-accent-info': colors.accent.info,
	'--atlas-accent-info-alpha-20': colors.accent.infoAlpha20,
	'--atlas-accent-success': colors.accent.success,
	'--atlas-accent-success-alt': colors.accent.successAlt,
	'--atlas-accent-success-alpha-10': colors.accent.successAlpha10,
	'--atlas-accent-nav-active': colors.accent.navActive,
	'--atlas-accent-wallet': colors.accent.walletAccent,
	'--atlas-accent-purple': colors.accent.purple,
	'--atlas-bg-app': colors.background.app,
	'--atlas-bg-app-alt': colors.background.appAlt,
	'--atlas-bg-app-deep': colors.background.appDeep,
	'--atlas-bg-surface': colors.background.surface,
	'--atlas-bg-surface-alt': colors.background.surfaceAlt,
	'--atlas-bg-surface-strong': colors.background.surfaceStrong,
	'--atlas-bg-surface-alpha-60': colors.background.surfaceAlpha60,
	'--atlas-bg-surface-selected': colors.background.surfaceSelected,
	'--atlas-bg-surface-raised': colors.background.surfaceRaised,
	'--atlas-bg-surface-accent': colors.background.surfaceAccent,
	'--atlas-bg-surface-interactive': colors.background.surfaceInteractive,
	'--atlas-bg-surface-hover': colors.background.surfaceHover,
	'--atlas-bg-surface-muted': colors.background.surfaceMuted,
	'--atlas-bg-card': colors.background.card,
	'--atlas-bg-dropdown': colors.background.dropdown,
	'--atlas-bg-dropdown-hover': colors.background.dropdownHover,
	'--atlas-bg-light': colors.background.light,
	'--atlas-bg-light-strong': colors.background.lightStrong,
	'--atlas-bg-light-translucent': colors.background.lightTranslucent,
	'--atlas-bg-disabled': colors.background.disabled,
	'--atlas-bg-white': colors.background.white,
	'--atlas-border-default': colors.border.default,
	'--atlas-border-muted': colors.border.muted,
	'--atlas-border-subtle': colors.border.subtle,
	'--atlas-border-strong': colors.border.strong,
	'--atlas-border-soft': colors.border.soft,
	'--atlas-border-contrast': colors.border.contrast,
	'--atlas-border-deep': colors.border.deep,
	'--atlas-border-dark': colors.border.dark,
	'--atlas-border-accent': colors.border.accent,
	'--atlas-border-accent-alt': colors.border.accentAlt,
	'--atlas-border-accent-strong': colors.border.accentStrong,
	'--atlas-border-light': colors.border.light,
	'--atlas-border-neutral': colors.border.neutral,
	'--atlas-text-primary': colors.text.primary,
	'--atlas-text-inverse': colors.text.inverse,
	'--atlas-text-dark': colors.text.dark,
	'--atlas-text-dark-strong': colors.text.darkStrong,
	'--atlas-text-muted': colors.text.muted,
	'--atlas-text-muted-alt': colors.text.mutedAlt,
	'--atlas-text-muted-strong': colors.text.mutedStrong,
	'--atlas-text-subtle': colors.text.subtle,
	'--atlas-text-subtle-alt': colors.text.subtleAlt,
	'--atlas-text-disabled': colors.text.disabled,
	'--atlas-text-light': colors.text.light,
	'--atlas-text-light-strong': colors.text.lightStrong,
	'--atlas-text-faint': colors.text.faint,
	'--atlas-text-faint-strong': colors.text.faintStrong,
	'--atlas-text-nav': colors.text.nav,
	'--atlas-text-highlight': colors.text.highlight,
	'--atlas-text-placeholder': colors.text.placeholder,
	'--atlas-text-navy': colors.text.navy,
	'--atlas-overlay-shadow': colors.overlay.shadow,
	'--atlas-overlay-neutral': colors.overlay.neutral,
	'--atlas-overlay-slate-alpha-35': colors.overlay.slateAlpha35,
	'--atlas-overlay-slate-alpha-40': colors.overlay.slateAlpha40,
	'--atlas-overlay-slate-alpha-08': colors.overlay.slateAlpha08,
	'--atlas-overlay-slate-alpha-12': colors.overlay.slateAlpha12,
	'--atlas-overlay-slate-alpha-20': colors.overlay.slateAlpha20,
	'--atlas-overlay-slate-alpha-90': colors.overlay.slateAlpha90,
	'--atlas-overlay-white-solid': colors.overlay.whiteSolid,
	'--atlas-google-blue': colors.google.blue,
	'--atlas-google-green': colors.google.green,
	'--atlas-google-yellow': colors.google.yellow,
	'--atlas-google-red': colors.google.red,
	'--atlas-slack-success': colors.slack.success,
	'--atlas-slack-error': colors.slack.error,
});

export const getTheme = (mode: PaletteMode = 'dark') => {
	const resolvedMode: AtlasThemeMode = mode === 'light' ? 'light' : 'dark';
	const colors = atlasColorTokens[resolvedMode];
	const atlasVars = buildAtlasVars(colors);

	return createTheme({
		palette: {
			mode,
			atlas: colors,
			background: {
				default: colors.background.app,
				paper: colors.background.surface,
			},
			primary: {
				main: colors.accent.primary,
				light: colors.accent.primarySoft,
				dark: colors.accent.primaryHover,
				contrastText: colors.text.primary,
			},
			secondary: {
				main: colors.accent.primaryAlt,
			},
			error: {
				main: colors.slack.error,
			},
			success: {
				main: colors.accent.successAlt,
			},
			warning: {
				main: colors.accent.walletAccent,
			},
			info: {
				main: colors.accent.info,
			},
			text: {
				primary: colors.text.primary,
				secondary: colors.text.muted,
				disabled: colors.text.disabled,
			},
			divider: colors.border.default,
		},
		typography: {
			allVariants: {
				textTransform: 'none',
				color: colors.text.primary,
				lineHeight: 'initial',
			},
			fontFamily: ['AmigaForever', 'Oxanium', 'sans-serif'].join(','),
			...variants,
			...fontWeights,
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					':root': atlasVars,
					body: {
						backgroundColor: colors.background.app,
						color: colors.text.primary,
					},
					'.MuiPaper-root, .MuiDialog-paper, .MuiMenu-paper, .MuiPopover-paper':
						{
							backgroundColor: colors.background.surface,
							backdropFilter: 'blur(30px) saturate(40%)',
							WebkitBackdropFilter: 'blur(30px) saturate(40%)',
							border: `1px solid ${colors.overlay.slateAlpha20}`,
						},
				},
			},
		},
	});
};
